/**
 * Connectivity, as a subscribable store rather than a hook that owns state.
 *
 * `useSyncExternalStore` is the right tool here and `useEffect` is not: this is genuinely an
 * external system with its own events, and the subscribe/getSnapshot pair means React reads the
 * current value during render instead of mirroring it into state one frame late.
 */

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function subscribeToOnline(listener: () => void): () => void {
  if (listeners.size === 0) {
    window.addEventListener('online', notify);
    window.addEventListener('offline', notify);
  }

  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('online', notify);
      window.removeEventListener('offline', notify);
    }
  };
}

/**
 * `navigator.onLine` is honest about "no network interface" and optimistic about everything else
 * — a phone on a hotspot with no upstream still reports true. It is the cheap signal; a failed
 * request is the authoritative one, which is why the base query maps a fetch failure to
 * `CLIENT_OFFLINE` rather than trusting this.
 */
export const getOnlineSnapshot = (): boolean => navigator.onLine;

/** Server-rendered snapshot. There is none — the app is a pure SPA — but React asks for it. */
export const getOnlineServerSnapshot = (): boolean => true;
